import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchReactivationList } from "@services/scheduling";
import { setDoNotCall } from "@services/reactivation";
import { isAdmin as checkIsAdmin } from "@utils/access";
import ErrorBanner from "@components/shared/ErrorBanner";
import ReactivationListSection from "@components/scheduling/ReactivationListSection";
import Button from "@components/ui/Button";

const SECTIONS = [
  { key: "recent_cancellations", title: "Cancellations & no-shows", selectable: true,
    showType: true, emptyText: "No recent cancellations or no-shows" },
  { key: "recently_seen", title: "Seen 1–4 months ago", selectable: true,
    emptyText: "Nobody seen 1–4 months ago without a booking" },
  { key: "dormant_patients", title: "Dormant 5–18 months", selectable: true,
    emptyText: "No dormant patients" },
  { key: "long_dormant", title: "Not seen in 18+ months", selectable: false,
    emptyText: "Nobody has been away 18 months or more" },
  { key: "inactive_pms", title: "Inactive in PMS", selectable: false,
    emptyText: "Nobody is marked inactive in the PMS" },
];

const PANEL_ID = "reactivation-list-panel";
const RANDOM_HINT_ID = "reactivation-random-hint";
const tabId = (key) => `reactivation-list-tab-${key}`;
const NO_ROWS = [];

/**
 * A list's title. The recently-seen range comes from the backend's summary
 * (`recently_seen_months`, e.g. [1, 4]) when it sends one, so the words follow
 * the rule if the rule moves.
 */
function titleOf(section, summary) {
  const range = section.key === "recently_seen" ? summary?.recently_seen_months : null;
  if (Array.isArray(range) && range.length === 2 && range.every(Number.isInteger)) {
    return `Seen ${range[0]}–${range[1]} months ago`;
  }
  return section.title;
}

/**
 * The call list for one clinic: a toolbar, a pinned row of list pills with
 * the selection's actions, and one list at a time. An admin's clinic comes
 * down from the page (the picker lives in the tab row); a clinic user's comes
 * from their token, so they send none.
 */
export default function ReactivationList({
  onStartCampaign,
  onStartRandomCampaign,
  selectedClinicId,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // A row action's failure is not the list's failure: it must not take the
  // table down with it, so it gets its own dismissible banner.
  const [rowError, setRowError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const Admin = useMemo(() => checkIsAdmin(), []);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [includeRecent, setIncludeRecent] = useState(false);
  const [busyPatientId, setBusyPatientId] = useState(null);
  // The list the user picked (null: the first one with rows) and the page
  // each view-only list is on.
  const [activeKey, setActiveKey] = useState(null);
  const [pages, setPages] = useState({});
  const tabRefs = useRef({});

  // A list that resolves after the admin moved to another clinic belongs to
  // the clinic they left; its stamp no longer matches and it is dropped.
  const reqRef = useRef(0);

  const clinicId = Admin ? selectedClinicId : undefined;

  // `silent` keeps the list on screen while it reloads (after a row action):
  // no spinner, and the tab, page and scroll position stay where they are. A
  // silent reload that fails says so in the row banner instead of swapping the
  // list for the retry pane.
  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (Admin && !clinicId) return;

    const reqId = ++reqRef.current;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await fetchReactivationList(clinicId, { includeRecent });
      if (reqRef.current !== reqId) return;
      setData(res);
    } catch (err) {
      if (reqRef.current !== reqId) return;
      const message = err.message || "Failed to load reactivation list";
      if (silent) setRowError(message);
      else setError(message);
    } finally {
      if (!silent && reqRef.current === reqId) setLoading(false);
    }
  }, [Admin, clinicId, includeRecent]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // external_patient_id is PMS-local and often a short integer, so "123" in
  // one clinic is a different person from "123" in the next. A selection that
  // survived the switch would silently pre-select the wrong patients. The
  // picked list and the pages belong to the clinic too.
  useEffect(() => {
    setSelectedIds(new Set());
    setActiveKey(null);
    setPages({});
  }, [clinicId]);

  const ruleOff = data?.summary?.long_dormant_rule_active === false;
  // An older backend sends fewer lists; a list that is absent is not offered
  // at all, and the 18-month list hides when the rule is off for the clinic
  // (history too short).
  const visibleSections = useMemo(
    () => SECTIONS
      .filter((s) => Array.isArray(data?.[s.key]) && !(s.key === "long_dormant" && ruleOff))
      .map((s) => ({ ...s, title: titleOf(s, data?.summary) })),
    [data, ruleOff]
  );

  const query = searchQuery.trim().toLowerCase();
  const searching = query.length > 0;
  // Each list's rows after the search, worked out once per data or search change.
  const filtered = useMemo(() => {
    const out = {};
    for (const s of visibleSections) {
      const rows = data?.[s.key] || NO_ROWS;
      out[s.key] = searching
        ? rows.filter(
            (p) => p.name?.toLowerCase().includes(query) || p.phone?.toLowerCase().includes(query)
          )
        : rows;
    }
    return out;
  }, [data, visibleSections, query, searching]);

  const totalOf = (s) => (data?.[s.key] || NO_ROWS).length;
  // Searching never moves the user to another list, so the default looks at
  // the full lists, not the matches.
  const active =
    visibleSections.find((s) => s.key === activeKey) ||
    visibleSections.find((s) => totalOf(s) > 0) ||
    visibleSections[0];

  // Every row from the callable lists, for selection.
  const allPatients = useMemo(() => {
    if (!data) return [];
    return SECTIONS.filter((s) => s.selectable).flatMap((s) => data[s.key] || []);
  }, [data]);

  // Rows the backend could enroll: a phone, not in a campaign, not marked do
  // not call, and not inside the cooldown. Rows the "Show recently contacted"
  // toggle reveals are still refused server-side, so they are never eligible.
  // A cooldown of 0 means none. One rule for "Select all" and the random draw.
  const cooldownDays = data?.summary?.cooldown_days || 0;
  const isEligible = useCallback((p) => {
    if (!p.phone || p.in_campaign || p.do_not_call) return false;
    if (cooldownDays > 0 && p.last_contacted_at) {
      const cutoff = Date.now() - cooldownDays * 86400000;
      if (Date.parse(p.last_contacted_at) >= cutoff) return false;
    }
    return true;
  }, [cooldownDays]);
  // A backend that sends the recently seen list also takes a random draw's
  // `list`. On it, "Random from this list" draws from the list on screen (the
  // backend gets its key): that list's eligible rows, whatever the search
  // shows, and a view-only list has nothing to draw. An older backend ignores
  // `list` and draws from cancellations and dormant together, so there the
  // button keeps its old name and behaviour ("Start with random patients"):
  // that combined pool from any pill, and no list named or sent, or the start
  // dialog would name a list the draw does not come from.
  const perListRandom = Array.isArray(data?.recently_seen);
  const randomPool = useMemo(() => {
    if (!data) return NO_ROWS;
    if (!perListRandom) {
      return [...(data.recent_cancellations || []), ...(data.dormant_patients || [])];
    }
    return active?.selectable ? data[active.key] || NO_ROWS : NO_ROWS;
  }, [data, perListRandom, active]);
  const eligibleCount = useMemo(
    () => randomPool.filter(isEligible).length, [randomPool, isEligible]);
  const startRandom = () =>
    perListRandom
      ? onStartRandomCampaign(eligibleCount, { key: active.key, title: active.title })
      : onStartRandomCampaign(eligibleCount);
  // Why the random button is off, in words on screen: a title on a disabled
  // button is not reliably shown or read out. Loading and a failed load
  // already say so in the pane below.
  let randomHint = null;
  if (data && !loading && !error && eligibleCount === 0) {
    if (!perListRandom) randomHint = "Nobody on the call list can be called right now";
    else if (active && !active.selectable)
      randomHint = "View only — pick a callable list to start a random campaign";
    else randomHint = "Nobody on this list can be called right now";
  }

  // Selection survives searching and switching lists, so start from every
  // row we know about.
  const selectedPatients = useMemo(
    () => allPatients.filter((p) => selectedIds.has(p.external_patient_id)),
    [allPatients, selectedIds]
  );
  const selectedIn = (s) =>
    s.selectable
      ? (data?.[s.key] || NO_ROWS).filter((p) => selectedIds.has(p.external_patient_id)).length
      : 0;

  const toggleOne = useCallback((patient) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(patient.external_patient_id))
        next.delete(patient.external_patient_id);
      else next.add(patient.external_patient_id);
      return next;
    });
  }, []);

  // A list's "Select all" hands up the rows it shows and can select; the
  // other list's selection is left alone.
  const toggleRows = useCallback((rows, select) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      rows.forEach((p) =>
        select
          ? next.add(p.external_patient_id)
          : next.delete(p.external_patient_id)
      );
      return next;
    });
  }, []);

  const markDoNotCall = async (patient) => {
    setBusyPatientId(patient.external_patient_id);
    setRowError(null);
    try {
      await setDoNotCall(patient.external_patient_id, {
        doNotCall: !patient.do_not_call,
        clinicId: Admin ? clinicId : undefined,
      });
      // The row's button stays disabled until the list has caught up.
      await loadData({ silent: true });
    } catch (e) {
      // Deliberately not `setError`: that swaps the whole table for a retry
      // pane, so one failed PUT would hide every patient.
      setRowError(e.message || "Could not update the patient");
    } finally {
      setBusyPatientId(null);
    }
  };

  const changeSearch = (value) => {
    setSearchQuery(value);
    // A new search starts every list again from its first page.
    setPages({});
  };

  const activate = (key, { focus = false } = {}) => {
    setActiveKey(key);
    const pill = tabRefs.current[key];
    if (focus) pill?.focus();
    // On a phone the pills scroll sideways; keep the chosen one in sight.
    pill?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  };

  const onTabKeyDown = (e) => {
    const keys = visibleSections.map((s) => s.key);
    const at = keys.indexOf(active.key);
    let next;
    if (e.key === "ArrowRight") next = (at + 1) % keys.length;
    else if (e.key === "ArrowLeft") next = (at - 1 + keys.length) % keys.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = keys.length - 1;
    else return;
    e.preventDefault();
    activate(keys[next], { focus: true });
  };

  const loadingPane = (
    <div role="status" className="flex items-center justify-center py-20">
      <svg
        className="animate-spin h-8 w-8 text-blue-600"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <span className="ml-3 text-gray-500">Loading reactivation list...</span>
    </div>
  );

  if (Admin && !clinicId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <svg
          className="w-12 h-12 mb-3 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9.27-3.11-11-7.5a11.72 11.72 0 013.168-4.477M9.88 9.88a3 3 0 104.24 4.24M15 12a3 3 0 00-3-3"
          />
        </svg>
        <p className="text-sm text-gray-500">
          Pick a clinic in the tab bar. Only clinics integrated with a scheduling
          provider are offered.
        </p>
      </div>
    );
  }

  const hiddenCooldown = data?.summary?.hidden_cooldown > 0 && !includeRecent
    ? data.summary.hidden_cooldown
    : 0;
  const nothingListed = visibleSections.every((s) => totalOf(s) === 0);
  const matchCount = (s) => (filtered[s.key] || NO_ROWS).length;
  const nothingMatches = searching && visibleSections.every((s) => matchCount(s) === 0);
  const showLists = !loading && !error && !nothingListed && Boolean(active);

  // The active list is empty under the search, but others are not: point there.
  const otherMatches = active
    ? visibleSections.filter((s) => s.key !== active.key && matchCount(s) > 0)
    : [];
  const activeEmptyText = searching ? (
    <span className="inline-flex flex-wrap items-center justify-center gap-2">
      <span>No matches here. Matches in:</span>
      {otherMatches.map((s) => (
        <Button key={s.key} color="light" size="xs" onClick={() => activate(s.key)}>
          {`${s.title} (${matchCount(s).toLocaleString()})`}
        </Button>
      ))}
    </span>
  ) : (
    active?.emptyText
  );

  return (
    <div className="flex flex-col gap-3 pb-24">
      <h1 className="sr-only">Call list</h1>

      {/* Toolbar: scrolls away with the page. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m21 21-4.35-4.35M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
            />
          </svg>
          <input
            type="text"
            aria-label="Search patients by name or phone"
            placeholder="Search name, phone..."
            value={searchQuery}
            onChange={(e) => changeSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <label
          className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap"
          title={hiddenCooldown
            ? `${hiddenCooldown} contacted in the last ${data.summary.cooldown_days} days`
            : undefined}
        >
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={includeRecent}
            onChange={(e) => setIncludeRecent(e.target.checked)}
          />
          Show recently contacted
          {hiddenCooldown > 0 && (
            <span className="text-gray-500">({hiddenCooldown.toLocaleString()} hidden)</span>
          )}
        </label>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <Button color="light" size="sm" onClick={() => loadData()}>
            Refresh
          </Button>
          <Button
            color="light"
            size="sm"
            disabled={loading || !!error || eligibleCount === 0}
            aria-describedby={randomHint ? RANDOM_HINT_ID : undefined}
            onClick={startRandom}
          >
            {perListRandom ? "Random from this list" : "Start with random patients"}
          </Button>
          {randomHint && (
            <span id={RANDOM_HINT_ID} className="w-full sm:w-auto text-right text-xs text-gray-500">
              {randomHint}
            </span>
          )}
        </div>
      </div>

      {rowError && (
        <ErrorBanner message={rowError} onDismiss={() => setRowError(null)} />
      )}

      {!loading && !error && ruleOff && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
          History covers {data.summary.history_coverage_months} months; the 18-month
          rule is off for this clinic.
        </p>
      )}

      {showLists && (
        // Pinned to the top of the page's scroller while the list scrolls
        // under it. The negative margins and offset cancel `main`'s p-6 (a
        // sticky box stops at the scroller's padding edge), so the bar runs
        // edge to edge and leaves no strip for rows to show through above it.
        // Solid white: a translucent bar let the rows under it ghost through.
        <div className="sticky -top-6 z-20 -mx-6 px-6 py-2 bg-white border-b border-gray-200 flex flex-wrap items-center gap-2">
          {/* Each pill is `relative` so its sr-only text (absolutely
              positioned) stays inside this scroller's clip; otherwise it
              widens the page on a phone. */}
          <div
            role="tablist"
            aria-label="Patient lists"
            className="flex gap-1 overflow-x-auto whitespace-nowrap max-w-full"
            onKeyDown={onTabKeyDown}
          >
            {visibleSections.map((s) => {
              const selected = s.key === active.key;
              const total = totalOf(s);
              const matches = matchCount(s);
              const picked = selectedIn(s);
              return (
                <button
                  key={s.key}
                  ref={(el) => { tabRefs.current[s.key] = el; }}
                  type="button"
                  role="tab"
                  id={tabId(s.key)}
                  aria-selected={selected}
                  aria-controls={PANEL_ID}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => activate(s.key)}
                  title={s.selectable ? undefined : "View only"}
                  className={`relative inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    selected
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {s.title}
                  {searching ? (
                    <>
                      <span aria-hidden="true" className={`tabular-nums ${selected ? "text-gray-300" : "text-gray-500"}`}>
                        {`${matches.toLocaleString()}/${total.toLocaleString()}`}
                      </span>
                      <span className="sr-only">
                        {`${matches.toLocaleString()} of ${total.toLocaleString()} match`}
                      </span>
                    </>
                  ) : (
                    <span className={`tabular-nums ${selected ? "text-gray-300" : "text-gray-500"}`}>
                      {total.toLocaleString()}
                    </span>
                  )}
                  {!s.selectable && (
                    <>
                      {/* The words where there is room for them (2xl); an
                          eye (look, don't touch) where there is not, to keep
                          the pills short. When the pills and the selection's
                          actions do not fit on one line, the bar wraps and
                          the actions move to a second line. */}
                      <span
                        aria-hidden="true"
                        className={`hidden 2xl:inline rounded px-1 text-[10px] font-semibold uppercase tracking-wide ${
                          selected ? "bg-white/15 text-gray-200" : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        View only
                      </span>
                      <svg
                        aria-hidden="true"
                        className={`hidden md:inline 2xl:hidden w-3.5 h-3.5 ${selected ? "text-gray-300" : "text-gray-400"}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                      <span className="sr-only">, view only</span>
                    </>
                  )}
                  {picked > 0 && (
                    <span className="bg-blue-600 text-white rounded-full px-1.5 text-xs tabular-nums">
                      <span aria-hidden="true">{picked.toLocaleString()}</span>
                      <span className="sr-only">{`, ${picked.toLocaleString()} selected`}</span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {selectedPatients.length > 0 && (
            <div className="ml-auto w-full sm:w-auto flex items-center justify-end gap-2">
              <span aria-live="polite" className="text-sm text-blue-900 whitespace-nowrap">
                {selectedPatients.length.toLocaleString()} patient
                {selectedPatients.length === 1 ? "" : "s"} selected
              </span>
              <Button color="light" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={() => onStartCampaign(selectedPatients)}
              >
                Start campaign
              </Button>
            </div>
          )}
        </div>
      )}

      {/* The per-list match counts, read out as the search narrows them. */}
      <div aria-live="polite" className="sr-only">
        {showLists && searching
          ? visibleSections
              .map((s) => `${s.title}: ${matchCount(s).toLocaleString()} of ${totalOf(s).toLocaleString()} match`)
              .join("; ")
          : ""}
      </div>

      {/* Loading and error both stay inside the layout so the controls stay
          put: a clinic the backend rejects must not blank the page, and a
          slow one must not flash the filters away on every switch. */}
      {loading ? (
        loadingPane
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button color="primary" size="md" onClick={() => loadData()}>
            Retry
          </Button>
        </div>
      ) : nothingListed || !active ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg
            className="w-12 h-12 mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm">No patients need reactivation</p>
        </div>
      ) : nothingMatches ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-sm text-gray-600">{`No patients match "${searchQuery.trim()}"`}</p>
          <Button color="light" size="sm" onClick={() => changeSearch("")}>
            Clear search
          </Button>
        </div>
      ) : (
        <div role="tabpanel" id={PANEL_ID} aria-labelledby={tabId(active.key)}>
          <ReactivationListSection
            key={active.key}
            title={active.title}
            rows={filtered[active.key] || NO_ROWS}
            selectable={active.selectable}
            showType={Boolean(active.showType)}
            isEligible={isEligible}
            selectedIds={selectedIds}
            onToggleOne={toggleOne}
            onToggleRows={toggleRows}
            onMarkDoNotCall={markDoNotCall}
            busyPatientId={busyPatientId}
            emptyText={activeEmptyText}
            page={pages[active.key] || 1}
            onPageChange={(page) => setPages((p) => ({ ...p, [active.key]: page }))}
          />
        </div>
      )}
    </div>
  );
}
